import React from 'react';
import PageLayout from '../components/global/PageLayout';
import useUser from '../hooks/global/useUser';
import ColoredAlert from '../components/global/ColoredAlert';
import useStripeSetUp from '../hooks/stripe/useStripeSetup';
import { isSudoManager } from '../utils/global/helper';
import OnboardingBanner from '../components/onboarding/OnboardingBanner';
import CenteredContentLayout from '../components/global/CenteredContentLayout';
import PageLoading from '../components/global/PageLoading';

export default function PaymentsPage() {
  const { user } = useUser();

  const {
    data,
    isFetching,
    isLinking,
    isGettingLogin,
    isLoading: isLoadingStripe,
    onStartSetup,
    onViewStripeAccount,
    refetch,
  } = useStripeSetUp();
  const { isOnboardComplete, isPendingReview, isDisabled, isIncomplete } =
    data || {};
  const isLoading = isLoadingStripe || data?.businessId !== user?.business?.id;

  const { title, description, buttonText, buttonAction } = React.useMemo(() => {
    switch (true) {
      case isIncomplete:
        return {
          title: 'Finish Connecting Your Account',
          description:
            'Your shop account with Stripe is not yet set up! Follow the link below to finish setting up your stripe account so you can receive payouts from Porter.',
          buttonText: 'Continue',
          buttonAction: onStartSetup,
        };
      case isDisabled:
        return {
          title: 'Your account is disabled',
          description:
            'Your shop account has been disabled. Please check your account or reach out to our support team for assistance.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      case isPendingReview:
        return {
          title: 'Your account is pending review',
          description:
            'Your shop account is pending review. Please check back later to see if your account has been approved. For any questions or assistance, reach out to our support team.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      case isOnboardComplete:
        return {
          title: 'Your account is connected!',
          description:
            'Your shop account is successfully connected through Stripe. Payouts will typically hit your bank account 2-4 business days after payment is made by the client. For any questions or assistance, reach out to our support team.',
          buttonText: 'View Stripe Account',
          buttonAction: onViewStripeAccount,
        };
      default:
        return {
          title: 'Set up your shop payments account',
          description:
            "Set up the payments account to transfer Shop funds to below. We use Stripe to make sure you get paid on time and to keep your bank details secured. Click Set up Payments below to set up your payments & connect your shop's bank account through Stripe.",
          buttonText: 'Set Up Payments',
          buttonAction: onStartSetup,
        };
    }
  }, [
    isDisabled,
    isIncomplete,
    isOnboardComplete,
    isPendingReview,
    onStartSetup,
    onViewStripeAccount,
  ]);

  return (
    <PageLayout
      topSection={<OnboardingBanner step="PAYMENTS" />}
      title="Payments Account"
      isLoading={isFetching && !isLoading}
      refresh={() => {
        refetch();
      }}
      guard={{
        isAllowed:
          !!user && isSudoManager(user) && user?.business.type === 'STUDIO',
      }}>
      <ColoredAlert
        animate={false}
        title="This is for the shops bank account!"
        subtitle="The payments account you set up below is where any money owed to the shop will be sent. If you personally tattoo, and need to set up a separate bank account for your own payments, you can do so in the Porter mobile app (available on iPhone and Android)"
        type="warning"
        dismissible
      />
      {isLoading ? (
        <PageLoading />
      ) : (
        <CenteredContentLayout
          image={{
            src: '/money-tree.gif',
            alt: 'payments',
          }}
          title={title}
          description={description}
          button={{
            text: buttonText,
            onClick: buttonAction,
            loading: isLinking || isGettingLogin,
          }}
        />
      )}
    </PageLayout>
  );
}
